import * as React from "react";
import { nbSippIncomePaymentDetailsLabels, nbSippIncomePaymentDetailsMessages } from "../../../Resource/Contents";
import IncomeRequired from "../../../../Common/SIPP/IncomePaymentDetails/IncomeRequired";
import RegularIncomePayment from "../../../../Common/SIPP/IncomePaymentDetails/RegularIncomePayment";
import IncomeFromPension from "./IncomeFromPension";
import { DrawdownType } from "../../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { PensionApply } from "../../../../../../../../Wrap/Models/Models/Processes/Apply/Pension/PensionApply";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../../Shared/Process/Process/ProcessComponent";
import { ChooseAssetsToSellTable } from "../../../../Common/SIPP/DrawdownDetails/ChooseAssetsToSellTable";
import { getChooseAssetsToSellTable, showChooseAssetstoSell } from "../../../../Common/SIPP/DrawdownDetails/Helper/DrawdownDetailHelper";
import { SectionHeader } from "../../SharedComponents";
import { MoneyDisplayHelper } from "../../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { CurrencyCode } from "../../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { RadiosEditor } from "../../../../../../../../react-core/Html/RadiosEditor";
import { ChooseAssetsToSellFor } from "../../../../Common/SIPP/DrawdownDetails/Resource/Contents";
import { ProcessType } from "../../../../../../../Shared/IST/Resource/Content";
import { processTypeEnum } from "../../../../Common/Resource/Contents";
import { setValueByKey } from "../../../../../../../Shared/Table/Utils/TableUtils";
import { calculateAnnualIncomeAmount, getTotalIncome } from "../../../../Common/SIPP/IncomePaymentDetails/Helper/IncomePaymentHelper"
import { PensionTransfer } from "../../../../../../../../Wrap/Models/Models/Processes/Quote/Pension/PensionTransfer";
import { InvestmentAllocation } from "../../../../../../../../Wrap/Models/Domains/Investment/InvestmentAllocation";
import { TransferType } from "../../../../../../../../Wrap/Constants/Enums/Domain/TransferType";
import { DistributionType } from "../../../../../../../../Wrap/Constants/Enums/DistributionType";
import { JsonDateParser } from "../../../../../../../../react-core/Utils/JsonDateParser";
import { IncomePaymentDestination } from "../../../../../../../../Wrap/Constants/Enums/Domain/IncomePaymentDestination";

interface IIncomePaymentProps extends IProcessComponentProps {
    data: PensionApply;
    onChange: (changes) => void;
    onIncomeChange: (changes) => void;
    isITFC: boolean;
}

interface IIncomePaymentState {
    showAssetTable: boolean;
}

export class IncomePayment extends ProcessComponent<IIncomePaymentProps, IIncomePaymentState> {
    constructor(props: any) {
        super(props);
        this.state = {
            showAssetTable: !this.renderAssetRadioButton(props.data),
        };
    }

    private hasDrawdown(data: PensionApply): boolean {
        return !!data?.DrawdownModel?.InstructionId;
    }

    private hasTransfer(data: PensionApply): boolean {
        return data?.PensionTransfers?.length > 0;
    }

    private allocateAcrossAllAssets(pensionTransfers: PensionTransfer[]): InvestmentAllocation[] {

        if (pensionTransfers?.length > 0) {
            let pensionTransfer = pensionTransfers[0];

            if (pensionTransfer.TransferType == TransferType.Cash) {
                let investmentAllocations = pensionTransfer.BuyAllocations?.map((allocation) => ({
                    Name: allocation.Name,
                    Percentage: allocation.Percentage,
                    ProductId: allocation.ProductId,
                    Type: allocation.Type
                }))

                return investmentAllocations;
            }
            else if (pensionTransfer.TransferType == TransferType.InSpecie) {
                const totalAmount = pensionTransfer.Instruments?.reduce((total, instrument) => total + instrument.QuotationValue.Amount, 0);

                let investmentAllocations = pensionTransfer.Instruments?.map((instrument) => ({
                    Name: instrument.Investment?.InstrumentCode,
                    Percentage: Math.floor((instrument.QuotationValue?.Amount / totalAmount) * 10000) / 100,
                    ProductId: instrument.Investment?.ProductId,
                    Type: instrument.Investment?.Type,
                }));

                let totalPercentage = investmentAllocations.reduce((sum, investmentAllocation) => sum + investmentAllocation.Percentage * 100, 0) / 100;
                let remainder = (100 * 100 - totalPercentage * 100) / 100;

                if (investmentAllocations.length > 0) {
                    for (let i = 0; i < Math.abs(remainder * 100); i++) {
                        if (remainder > 0) {
                            investmentAllocations[i % investmentAllocations.length].Percentage = (investmentAllocations[i % investmentAllocations.length].Percentage * 100 + 1) / 100;
                        } else {
                            investmentAllocations[i % investmentAllocations.length].Percentage = (investmentAllocations[i % investmentAllocations.length].Percentage * 100 - 1) / 100;
                        }
                    }
                }

                return investmentAllocations;
            }
            
        }

        return [];
    }


    handleAllocationChange(changes, isAcrossAllAssets = null) {
        const { onChange, data } = this.props;
        let _data = JSON.parse(JSON.stringify(data));
        if (this.hasDrawdown(data)) {
            for (const key in changes) {
                _data = setValueByKey(_data, `DrawdownModel.${key}`, changes[key]);
            }
        }
        if (this.hasTransfer(data)) {
            _data.PensionTransfers = _data?.PensionTransfers?.map((e) => {
                for (const key in changes) {
                    e = setValueByKey(e, `IncomePaymentDetails.DrawdownIncome.${key}`, changes[key]);
                }
                if (isAcrossAllAssets != null) {
                    e.IncomePaymentDetails.DrawdownIncome.AnnualIncome.IsAcrossAllAssets = isAcrossAllAssets
                }
                return e;
            });
        }
        onChange(_data);
    }

    renderAssetRadioButton(data: PensionApply) {
        if (data?.DrawdownModel?.IsAcrossAllAssets == false || ( data?.PensionTransfers?.length > 0 &&
            (data?.PensionTransfers[0]?.IncomePaymentDetails?.IncomeDisinvestmentAssetSelectionMethod != DistributionType.ApplyProRata || !data?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.IsAcrossAllAssets))) {
                return false;
            }

        return true;
    }

    public render(): JSX.Element {
        const { data, onChange, showErrorMsgPopUp, onIncomeChange, isITFC } = this.props;
        const paymentDates = [JsonDateParser.toDate(data?.ClientSinglePayment?.PaymentDate),
            JsonDateParser.toDate(data?.EmployerSalaryDeductionSinglePayment?.PaymentDate),
            JsonDateParser.toDate(data?.ThirdPartySinglePayment?.PaymentDate),
            JsonDateParser.toDate(data?.EmployerSinglePayment?.PaymentDate)];

        const incomePaymentAllocations = data?.PensionTransfers?.[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.IncomePaymentAllocations ||
            data?.DrawdownModel?.Income?.IncomePaymentAllocations;
        const hasBankDestination = incomePaymentAllocations?.some(alloc => alloc.Destination == IncomePaymentDestination.BankAccount);
        return (
            <>
                <IncomeRequired
                    data={
                        data?.DrawdownModel?.Income ||
                        (data?.PensionTransfers?.length > 0 &&
                            data?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome)
                    }
                    onChange={(changes) => this.onIncomeChangeOverride(changes, onIncomeChange)}
                    labels={nbSippIncomePaymentDetailsLabels}
                    messages={nbSippIncomePaymentDetailsMessages}
                    onRef={(ref) => this.appendChild(ref)}
                    drawdownType={data?.DrawdownModel?.DrawdownType}
                    pensionTransfer={data?.PensionTransfers}
                    regularCrystalDifferentInital={
                        data?.DrawdownModel?.RegularCrystallisationDetails?.HasADifferentInitialAmount
                    }
                    pensionBase={data}
                    processType={processTypeEnum.NewBusiness}
                    isITFC={isITFC}
                />
                <hr />
                <IncomeFromPension
                    data={data}
                    showErrorMsgPopUp={this.props.showErrorMsgPopUp}
                    onChange={(changes) => onChange(changes)}
                    onRef={(ref) => this.appendChild(ref)}
                    isITFC={isITFC}
                    processType={processTypeEnum.NewBusiness}
                />
                <RegularIncomePayment
                    data={
                        data?.DrawdownModel?.Income ||
                        (data?.PensionTransfers?.length > 0 &&
                            data?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome)
                    }
                    onChange={(changes) => this.onIncomeChangeOverride(changes, onIncomeChange)}
                    labels={nbSippIncomePaymentDetailsLabels}
                    messages={nbSippIncomePaymentDetailsMessages}
                    onRef={(ref) => this.appendChild(ref)}
                    drawdownType={data?.DrawdownModel?.DrawdownType}
                    availableCash={data?.DrawdownModel?.CrystallisationDetails?.AvailableCash?.Amount}
                    taxFreeLumpSum={data?.DrawdownModel?.TaxFreeLumpSum?.Amount}
                    regularCrystallisationDetails={data?.DrawdownModel?.RegularCrystallisationDetails}
                    shouldHidePaymentMethod={!hasBankDestination}
                    firstPaymentDateAtLeast={paymentDates.every(date => !date) 
                        ? null 
                        : paymentDates.filter(date => date).reduce((min, date) => date < min ? date : min)}
                    pensionBase={data}
                    processType={processTypeEnum.NewBusiness}
                    isITFC={isITFC}
                    sellableAssets={getChooseAssetsToSellTable(data)}
                />
                {data?.DrawdownModel?.DrawdownType !== DrawdownType.Regular && showChooseAssetstoSell(data.DrawdownModel, ChooseAssetsToSellFor.Income) && (
                    <>
                        {SectionHeader("Choose assets to sell to pay income")}
                        <p>
                            Disinvestment from post-retirement to meet income payments
                            <span>
                                (
                                {MoneyDisplayHelper.getMoneyFormat({
                                    Amount: getTotalIncome(data?.DrawdownModel, null, data),
                                    Currency: CurrencyCode.GBP,
                                })}
                                )
                            </span>
                        </p>
                        <RadiosEditor
                            options={[true, false]}
                            label={"Choose asset selection method"}
                            getText={(v) => (v ? "Proportionally across all assets" : "From specified assets")}
                            onChange={(v) => {
                                this.handleAllocationChange({
                                    IncomeDisinvestmentAllocations: v ? this.allocateAcrossAllAssets(data?.PensionTransfers) : [],
                                    IsAcrossAllAssets: v,
                                }, v);
                                this.setState({ showAssetTable: !v });
                            }}
                            tooltip={
                                "If there is insufficient value in an asset at the time of the sale, we will default to disinvesting proportioanlly from the assets selected."
                            }
                            cssWrapper={"rg-inline mb-10"}
                            selected={this.renderAssetRadioButton(data)}
                        />
                    </>
                )}
                {this.state.showAssetTable && showChooseAssetstoSell(data.DrawdownModel, ChooseAssetsToSellFor.Income) && (
                    <ChooseAssetsToSellTable
                        modelData={data?.DrawdownModel}
                        data={
                            data?.DrawdownModel?.IncomeDisinvestmentAllocations ||
                            (data?.PensionTransfers?.length > 0 &&
                                data?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome
                                    ?.IncomeDisinvestmentAllocations)
                        }
                        tableData={getChooseAssetsToSellTable(data)}
                        onRef={(ref) => this.appendChild(ref)}
                        onChange={(v) => {
                            this.handleAllocationChange({ IncomeDisinvestmentAllocations: v });
                        }}
                        reason={ChooseAssetsToSellFor.Income}
                        showErrorMsgPopUp={(msg, isShow) => showErrorMsgPopUp(msg, isShow)}
                        processType={ProcessType.NB}
                        transferData={data?.PensionTransfers}
                        disableOffPlatform={true}
                        pensionBase={data}
                    />
                )}
            </>
        );
    }

    private onIncomeChangeOverride = (changes, callback: (x) => void) => {
        const { data } = this.props;

        const annualIncome = data?.DrawdownModel?.Income ??
            (data?.PensionTransfers?.length > 0 ? data?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome : null);
        let grossIncomePerPayment = changes['GrossIncomePerPayment'] ?? annualIncome?.GrossIncomePerPayment;
        let incomeFrequency = changes['Frequency'] ?? annualIncome?.Frequency;

        if (grossIncomePerPayment?.Amount != null && grossIncomePerPayment?.Amount != undefined &&
            incomeFrequency != null && incomeFrequency != undefined) {
            changes.AnnualAmount = {
                Amount: calculateAnnualIncomeAmount(grossIncomePerPayment.Amount, incomeFrequency)
            };
        }
        callback(changes);
    }
}

export default IncomePayment;
